// Three actions visible on every saved/loaded eye: Download PNG,
// Copy palette, Set as starting point. Used in two sizes:
//   - full size in EyePreview (horizontal bar with labels)
//   - icon-only in CatalogueCard (corner icon buttons, tooltips on hover)

import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { useToast } from './Toast';
import {
  IconBookmark,
  IconDownload,
  IconPalette,
  IconSpinner,
} from './Icon';

export interface DownloadBarProps {
  /** ID of the image (used by download / palette endpoints). */
  imageId: string;
  /** Optional catalogue id — when present, palette and "set as starting
   *  point" are enabled; when absent we only allow download. */
  catalogueId?: string | null;
  /** Display label used in the downloaded filename. */
  title?: string | null;
  /** Visual density — `'full'` for Studio preview, `'compact'` for cards. */
  variant?: 'full' | 'compact';
  /** Hide "Set as starting point" in the Studio (Studio IS the starting point). */
  hideSetAsStarting?: boolean;
  /** Hook fired when the user clicks "Set as starting point" — parent
   *  component updates studio state and switches tab. */
  onSetAsStarting?: () => void | Promise<void>;
}

function safeFilename(title: string | null | undefined, id: string): string {
  const base = (title ?? '').trim();
  const slug =
    base
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 48) || `eye-${id.slice(0, 8)}`;
  return `stamps-${slug}.png`;
}

export function DownloadBar(props: DownloadBarProps) {
  const {
    imageId,
    catalogueId,
    title,
    variant = 'full',
    hideSetAsStarting = false,
    onSetAsStarting,
  } = props;

  const toast = useToast();
  const [busy, setBusy] = useState<'download' | 'palette' | 'set' | null>(
    null,
  );

  const canPalette = Boolean(catalogueId);
  const canSetAsStarting = !hideSetAsStarting && Boolean(catalogueId) && Boolean(onSetAsStarting);

  async function handleDownload() {
    setBusy('download');
    try {
      const blob = await api.downloadImage(imageId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = safeFilename(title ?? null, imageId);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so Safari can pick the file up reliably.
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      toast.success('Download started', safeFilename(title ?? null, imageId));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Download failed';
      toast.error('Could not download', msg);
    } finally {
      setBusy(null);
    }
  }

  async function handleCopyPalette() {
    if (!catalogueId) return;
    setBusy('palette');
    try {
      const palette = await api.getPalette(catalogueId);
      if (!Array.isArray(palette) || palette.length === 0) {
        throw new ApiError(
          'invalid_palette',
          'Backend returned no colours',
          200,
        );
      }
      const csv = palette.join(', ');
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(csv);
      } else {
        // Fallback for browsers without async clipboard API.
        const ta = document.createElement('textarea');
        ta.value = csv;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(
        'Palette copied — paste into Procreate',
        palette.join(' · '),
      );
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not fetch palette';
      toast.error('Could not copy palette', msg);
    } finally {
      setBusy(null);
    }
  }

  async function handleSetAsStarting() {
    if (!onSetAsStarting) return;
    setBusy('set');
    try {
      await onSetAsStarting();
    } finally {
      setBusy(null);
    }
  }

  const isCompact = variant === 'compact';

  return (
    <div
      role="group"
      aria-label="Eye actions"
      className={[
        isCompact
          ? 'flex items-center gap-1'
          : 'flex flex-wrap items-center gap-2',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy !== null}
        aria-label={`Download PNG${title ? `: ${title}` : ''}`}
        title="Download PNG"
        className={[
          'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border transition-colors duration-150',
          'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100',
          'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          'disabled:cursor-not-allowed disabled:opacity-60',
          isCompact ? 'h-11 w-11' : 'h-11 px-3',
        ].join(' ')}
      >
        {busy === 'download' ? (
          <IconSpinner size={16} className="animate-spin" aria-hidden />
        ) : (
          <IconDownload size={16} aria-hidden />
        )}
        {!isCompact && <span className="text-sm">Download PNG</span>}
      </button>

      <button
        type="button"
        onClick={handleCopyPalette}
        disabled={!canPalette || busy !== null}
        aria-label={
          canPalette
            ? 'Copy 5-colour palette to clipboard'
            : 'Palette is available for saved eyes'
        }
        title={canPalette ? 'Copy palette' : 'Save the eye first to copy its palette'}
        className={[
          'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border transition-colors duration-150',
          'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-100',
          'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          'disabled:cursor-not-allowed disabled:opacity-60',
          isCompact ? 'h-11 w-11' : 'h-11 px-3',
        ].join(' ')}
      >
        {busy === 'palette' ? (
          <IconSpinner size={16} className="animate-spin" aria-hidden />
        ) : (
          <IconPalette size={16} aria-hidden />
        )}
        {!isCompact && <span className="text-sm">Copy palette</span>}
      </button>

      {canSetAsStarting && (
        <button
          type="button"
          onClick={handleSetAsStarting}
          disabled={busy !== null}
          aria-label={`Set ${title ?? 'eye'} as starting point`}
          title="Set as starting point"
          className={[
            'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border transition-colors duration-150',
            'border-accent/40 bg-accent/10 text-accent hover:bg-accent/20',
            'dark:border-accent/50 dark:bg-accent/15 dark:text-accent dark:hover:bg-accent/25',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            'disabled:cursor-not-allowed disabled:opacity-60',
            isCompact ? 'h-11 w-11' : 'h-11 px-3',
          ].join(' ')}
        >
          {busy === 'set' ? (
            <IconSpinner size={16} className="animate-spin" aria-hidden />
          ) : (
            <IconBookmark size={16} aria-hidden />
          )}
          {!isCompact && <span className="text-sm">Set as starting point</span>}
        </button>
      )}
    </div>
  );
}
